'use client'

import React, { useState, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { X, Filter } from 'lucide-react'
import { terpeneApi } from '@/lib/api/terpenes/supabase-client'

interface TerpeneFiltersProps {
  selectedCategory: string | null
  selectedEffects: string[]
  selectedSources: string[]
  onCategoryChange: (category: string | null) => void
  onEffectsChange: (effects: string[]) => void
  onSourcesChange: (sources: string[]) => void
  onClearAll: () => void
}

export default function TerpeneFilters({
  selectedCategory,
  selectedEffects,
  selectedSources,
  onCategoryChange,
  onEffectsChange,
  onSourcesChange,
  onClearAll
}: TerpeneFiltersProps) {
  const [categories, setCategories] = useState<string[]>([])
  const [availableEffects, setAvailableEffects] = useState<string[]>([])
  const [availableSources, setAvailableSources] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<'category' | 'effects' | 'sources'>('category')
  const [loading, setLoading] = useState(true)

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true)
        const [cats, effs, srcs] = await Promise.all([
          terpeneApi.getCategories(),
          terpeneApi.getEffects(),
          terpeneApi.getSources()
        ])
        
        setCategories(cats)
        setAvailableEffects(effs.slice(0, 10)) // Limit to top 10 for UI
        setAvailableSources(srcs.slice(0, 10)) // Limit to top 10 for UI
      } catch (error) {
        console.error('Failed to load filter options:', error)
      } finally {
        setLoading(false)
      }
    }

    loadFilterOptions()
  }, [])

  const toggleEffect = (effect: string) => {
    if (selectedEffects.includes(effect)) {
      onEffectsChange(selectedEffects.filter(e => e !== effect))
    } else {
      onEffectsChange([...selectedEffects, effect])
    }
  }

  const toggleSource = (source: string) => {
    if (selectedSources.includes(source)) {
      onSourcesChange(selectedSources.filter(s => s !== source))
    } else {
      onSourcesChange([...selectedSources, source])
    }
  }

  const hasActiveFilters = selectedCategory || selectedEffects.length > 0 || selectedSources.length > 0

  return (
    <div className="space-y-3">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-white/60" />
          <span className="text-sm font-medium text-white/80">Filters</span>
        </div>
        {hasActiveFilters && (
          <Button
            onClick={onClearAll}
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-white/60 hover:text-white hover:bg-white/10"
          >
            Clear all
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3 bg-black/20">
          <TabsTrigger 
            value="category"
            className="text-xs data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300"
          >
            Category
            {selectedCategory && (
              <Badge className="ml-1 h-4 px-1 text-xs bg-emerald-500/30">1</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="effects"
            className="text-xs data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300"
          >
            Effects
            {selectedEffects.length > 0 && (
              <Badge className="ml-1 h-4 px-1 text-xs bg-emerald-500/30">
                {selectedEffects.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="sources"
            className="text-xs data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300"
          >
            Found in
            {selectedSources.length > 0 && (
              <Badge className="ml-1 h-4 px-1 text-xs bg-emerald-500/30">
                {selectedSources.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filter Content */}
      <div className="min-h-[100px] max-h-[150px] overflow-y-auto scroll-safe">
        {loading ? (
          <div className="flex items-center justify-center h-20">
            <span className="text-xs text-white/40">Loading filters...</span>
          </div>
        ) : (
          <>
            {/* Category Filters */}
            {activeTab === 'category' && (
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    onClick={() => onCategoryChange(selectedCategory === category ? null : category)}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    className={`
                      h-7 px-3 text-xs
                      ${selectedCategory === category 
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30' 
                        : 'bg-transparent text-white/60 border-white/20 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    {category}
                    {selectedCategory === category && (
                      <X className="ml-1 h-3 w-3" />
                    )}
                  </Button>
                ))}
              </div>
            )}

            {/* Effects Filters */}
            {activeTab === 'effects' && (
              <div className="flex flex-wrap gap-2">
                {availableEffects.map((effect) => (
                  <Button
                    key={effect}
                    onClick={() => toggleEffect(effect)}
                    variant={selectedEffects.includes(effect) ? "default" : "outline"}
                    size="sm"
                    className={`
                      h-7 px-3 text-xs
                      ${selectedEffects.includes(effect)
                        ? 'bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30' 
                        : 'bg-transparent text-white/60 border-white/20 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    {effect}
                    {selectedEffects.includes(effect) && (
                      <X className="ml-1 h-3 w-3" />
                    )}
                  </Button>
                ))}
              </div>
            )}

            {/* Found in Filters */}
            {activeTab === 'sources' && (
              <div className="flex flex-wrap gap-2">
                {availableSources.map((source) => (
                  <Button
                    key={source}
                    onClick={() => toggleSource(source)}
                    variant={selectedSources.includes(source) ? "default" : "outline"}
                    size="sm"
                    className={`
                      h-7 px-3 text-xs
                      ${selectedSources.includes(source)
                        ? 'bg-green-500/20 text-green-300 border-green-500/30 hover:bg-green-500/30' 
                        : 'bg-transparent text-white/60 border-white/20 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    {source}
                    {selectedSources.includes(source) && (
                      <X className="ml-1 h-3 w-3" />
                    )}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Active Filter Summary */}
      {hasActiveFilters && (
        <div className="pt-2 border-t border-white/10">
          <div className="flex flex-wrap gap-1">
            {selectedCategory && (
              <Badge 
                variant="secondary" 
                className="text-xs bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
              >
                {selectedCategory}
              </Badge>
            )}
            {selectedEffects.map(effect => (
              <Badge 
                key={effect}
                variant="secondary" 
                className="text-xs bg-blue-500/10 text-blue-300 border-blue-500/20"
              >
                {effect}
              </Badge>
            ))}
            {selectedSources.map(source => (
              <Badge 
                key={source}
                variant="secondary" 
                className="text-xs bg-green-500/10 text-green-300 border-green-500/20"
              >
                {source}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}